import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2, Building2 } from "lucide-react";
import { trackPixel } from "@/lib/metaPixel";

const LOGIN_BG = "https://images.pexels.com/photos/35215140/pexels-photo-35215140.jpeg";
const SUDEPORTE_LOGO = "/brand/sudefootball-white-40.png";

const RESERVED_HOSTS = new Set(["www", "sudeporte", "app", "api", "localhost"]);

function detectClubSubdomain() {
  // Sudefootball uses a single unified domain (sudefootball.sudeporte.com).
  // Legacy per-club subdomain detection is disabled but kept for backward compat.
  return null;
}

export default function Login() {
  // Conversión Meta: Stripe redirige aquí con ?familia=ok tras el pago del plan Player
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("familia") === "ok" && !sessionStorage.getItem("px_purchase")) {
      sessionStorage.setItem("px_purchase", "1");
      trackPixel("Purchase", { value: 12, currency: "EUR", content_name: "sudefootball_player" });
    }
  }, []);
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [clubContext, setClubContext] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const sub = detectClubSubdomain();
    if (!sub) return;
    (async () => {
      try {
        const { data } = await api.get(`/clubs/by-subdomain/${sub}`);
        setClubContext(data);
      } catch (e) { /* silent — fall back to generic login */ }
    })();
  }, []);

  if (user) return <Navigate to="/home" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bienvenido, " + res.user.name);
      navigate("/home");
    } else {
      toast.error(res.error || "No se pudo iniciar sesión");
    }
  };

  const fillDemo = (kind) => {
    const map = {
      admin: ["gescomsport@gmail.com", "Admin1234!"],
      club: ["admin@cfdemo.com", "Demo1234!"],
      coord: ["coordinador@cfdemo.com", "Demo1234!"],
      coach: ["entrenador1@cfdemo.com", "Demo1234!"],
    };
    setEmail(map[kind][0]);
    setPassword(map[kind][1]);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={LOGIN_BG} alt="football" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute top-6 right-6 z-20 flex flex-col items-end gap-1 leading-none">
          <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-8 w-auto object-contain" />
          <span className="font-heading font-black italic tracking-tight leading-none text-white text-xs">sudeporte</span>
        </div>
        <div className="absolute inset-0 flex flex-col justify-between p-12 z-10">
          <div className="flex flex-col items-start leading-none">
            <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-11 w-auto object-contain" />
            <span className="font-heading font-black italic tracking-tight leading-none text-slate-900 text-base mt-2">sudeporte</span>
          </div>
          <div>
            <div className="overline mb-3">Análisis Jugadores Fútbol</div>
            <h1 className="font-heading font-extrabold text-white text-5xl xl:text-6xl leading-[0.95] tracking-tight">
              Cada indicador.<br/>
              <span className="text-primary">Cada jornada.</span><br/>
              Cada jugador.
            </h1>
            <p className="mt-6 text-base text-white/70 max-w-md">
              El módulo de análisis para entrenadores, coordinadores y direcciones deportivas de los clubes en la plataforma Sudefootball.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 text-white/80">
            <div>
              <div className="metric-number text-4xl text-primary">+100</div>
              <div className="overline mt-1">Clubes en Sudefootball</div>
            </div>
            <div>
              <div className="metric-number text-4xl">7</div>
              <div className="overline mt-1">Categorías U6-U16</div>
            </div>
            <div>
              <div className="metric-number text-4xl">3</div>
              <div className="overline mt-1">Roles por club</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="flex lg:hidden flex-col items-start leading-none mb-8">
            <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-10 w-auto" />
            <span className="font-heading font-black italic tracking-tight leading-none text-foreground text-xs mt-2">sudeporte</span>
          </div>

          {clubContext && (
            <div data-testid="club-context-banner" className="mb-8 border border-primary/60 bg-primary/5 p-4 flex items-start gap-3">
              <div className="w-9 h-9 border border-primary/60 flex items-center justify-center shrink-0">
                <Building2 size={16} className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="overline text-primary">Estás entrando a</div>
                <div className="font-heading font-black text-xl uppercase tracking-tight truncate">{clubContext.name}</div>
                <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{clubContext.name}</div>
              </div>
            </div>
          )}

          <div className="mb-10">
            <div className="overline mb-2">Acceso privado</div>
            <h2 className="font-heading font-black text-5xl tracking-tighter uppercase">
              Inicia sesión
            </h2>
            <p className="text-sm text-muted-foreground mt-3">
              {clubContext
                ? `Introduce tus credenciales para entrar al panel de ${clubContext.name}.`
                : "Introduce tus credenciales para acceder al panel de tu club."}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@club.com"
                className="w-full h-11 bg-transparent border border-border px-3 focus:border-primary focus:outline-none transition-colors duration-200 font-mono text-sm"
              />
            </div>
            <div>
              <label className="overline block mb-2">Contraseña</label>
              <input
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 bg-transparent border border-border px-3 focus:border-primary focus:outline-none transition-colors duration-200 font-mono text-sm"
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="btn-volt w-full justify-center"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>Entrar <ArrowRight size={16} /></>
              )}
            </button>

            <div className="text-right">
              <a
                href="/forgot-password"
                data-testid="forgot-password-link"
                className="text-[11px] font-heading uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors duration-200"
              >
                ¿Olvidaste tu contraseña?
              </a>
            </div>
          </form>

          {!clubContext && (
            <div className="mt-6">
              <button
                data-testid="login-video-btn"
                onClick={() => setShowVideo(true)}
                className="w-full h-11 border border-primary/50 text-primary text-xs font-heading font-black uppercase tracking-widest hover:bg-primary/10 transition-colors duration-200"
              >
                ▶ Mira Sudefootball en 2 minutos
              </button>
            </div>
          )}

          {!clubContext && (
            <div className="mt-10 pt-6 border-t border-border">
              <div className="overline mb-3">Cuentas demo (para clubes)</div>
              <div className="grid grid-cols-3 gap-2">
                <button data-testid="demo-club-btn" onClick={() => fillDemo("club")} className="h-9 border border-border text-xs font-heading uppercase tracking-wider hover:border-primary hover:text-primary transition-colors duration-200">
                  Admin Club
                </button>
                <button data-testid="demo-coord-btn" onClick={() => fillDemo("coord")} className="h-9 border border-border text-xs font-heading uppercase tracking-wider hover:border-primary hover:text-primary transition-colors duration-200">
                  Coordinador
                </button>
                <button data-testid="demo-coach-btn" onClick={() => fillDemo("coach")} className="h-9 border border-border text-xs font-heading uppercase tracking-wider hover:border-primary hover:text-primary transition-colors duration-200">
                  Entrenador
                </button>
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center justify-center gap-2 text-[10px] text-muted-foreground overline">
            <img src="/brand/sudefootball-white-40.png" alt="Sudefootball" className="h-4 w-auto object-contain" />
            <span>by</span>
            <span className="font-heading font-black italic tracking-tight leading-none text-foreground text-[10px]">sudeporte</span>
          </div>
        </div>
      </div>

      {showVideo && (
        <div
          data-testid="login-video-modal"
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="relative w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="login-video-close"
              onClick={() => setShowVideo(false)}
              className="absolute -top-10 right-0 text-white text-sm font-heading uppercase tracking-widest hover:text-primary"
            >
              ✕ Cerrar
            </button>
            <video
              src="/descargas/sudefootball-3min.mp4"
              controls
              autoPlay
              playsInline
              className="w-full max-h-[80vh] border border-white/20 bg-black"
            />
          </div>
        </div>
      )}
    </div>
  );
}
